import { WorkHistoryOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Result from 'components/v2/common/Result';
import { ProfileFields } from './profileFields';
import * as S from '../ClientProfile.styled';
import { WorkExperienceData } from '../ClientProfile';

import { OCCUPATION_CODE_KEYS } from './profileFieldUtils';
import { RootState } from 'redux/store';

type WorkExperienceProps = {
    workExperienceData: null | WorkExperienceData[];
    spouseWorkExperienceData: null | WorkExperienceData[];
    mobile: boolean;
    spouseOnly?: boolean;
};

/** Matches AssessmentV2/WorkExperienceV2 destination-country conditions. */
const REGION_COUNTRIES = ['Australia', 'Australie', 'Canada', 'United Kingdom', 'Royaume Uni'];

const WorkExperience = ({
    workExperienceData,
    spouseWorkExperienceData,
    mobile,
    spouseOnly = false,
}: WorkExperienceProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const hasWorkExperienceData = !spouseOnly && !!workExperienceData?.length;
    const hasSpouseWorkExperienceData = !!spouseWorkExperienceData?.length;
    function formatDate(date: Date | string | null) {
        if (date) {
            const stringDate = date.toString();
            const [year, month, day] = stringDate.split('-');
            const months =
                language === 'EN'
                    ? [
                          'January',
                          'February',
                          'March',
                          'April',
                          'May',
                          'June',
                          'July',
                          'August',
                          'September',
                          'October',
                          'November',
                          'December',
                      ]
                    : [
                          'Janvier',
                          'Février',
                          'Mars',
                          'Avril',
                          'Mai',
                          'Juin',
                          'Juillet',
                          'Août',
                          'Septembre',
                          'Octobre',
                          'Novembre',
                          'Décembre',
                      ];

            const monthName = months[parseInt(month, 10) - 1];
            const formattedDate = `${monthName} ${parseInt(day, 10)}, ${year}`;
            return formattedDate;
        } else {
            return '';
        }
    }

    return (
        <>
            {hasWorkExperienceData ? (
                <>
                    {hasSpouseWorkExperienceData && (
                        <S.Title>{t('applicant', { lng: language })}</S.Title>
                    )}

                    {workExperienceData?.map((experience: WorkExperienceData, index: number) => {
                        const showRegion = REGION_COUNTRIES.includes(experience.country as string);
                        // Form shows work permit only for destination countries.
                        const showWorkPermit = showRegion;
                        const extraFieldKeys = [
                            'occupation_ca',
                            'occupation_au',
                            'occupation_uk',
                            ...OCCUPATION_CODE_KEYS,
                            'zip',
                            'self_employed',
                        ];

                        return (
                            <div key={index}>
                                {workExperienceData && workExperienceData.length > 1 && (
                                    <S.Title>{`${t('experience', { lng: language })} ${
                                        index + 1
                                    }`}</S.Title>
                                )}
                                <S.InfoItem>
                                    <Tooltip title={t('startDate', { lng: language })}>
                                        <S.Label>{t('startDate', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={formatDate(experience.start)}>
                                        <S.Info mobile={mobile}>
                                            {formatDate(experience.start)}
                                        </S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('currentEmployment', { lng: language })}>
                                        <S.Label>
                                            {mobile
                                                ? t('current', { lng: language })
                                                : t('currentEmployment', { lng: language })}
                                        </S.Label>
                                    </Tooltip>
                                    <Tooltip
                                        title={
                                            experience.current
                                                ? t('yes', { lng: language })
                                                : t('no', { lng: language })
                                        }
                                    >
                                        <S.Info mobile={mobile}>
                                            {experience.current
                                                ? t('yes', { lng: language })
                                                : t('no', { lng: language })}
                                        </S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <S.InfoItem>
                                    <Tooltip title={t('endDate', { lng: language })}>
                                        <S.Label>{t('endDate', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={formatDate(experience.end)}>
                                        <S.Info mobile={mobile}>
                                            {formatDate(experience.end)}
                                        </S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                {showWorkPermit && (
                                    <S.InfoItem>
                                        <Tooltip title={t('employerPermit', { lng: language })}>
                                            <S.Label mobile={mobile}>
                                                {t('employerPermit', { lng: language })}
                                            </S.Label>
                                        </Tooltip>
                                        <Tooltip title={experience.work_permit}>
                                            <S.Info mobile={mobile}>
                                                {experience.work_permit}
                                            </S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                )}
                                <S.InfoItem>
                                    <Tooltip title={t('country', { lng: language })}>
                                        <S.Label>{t('country', { lng: language })}</S.Label>
                                    </Tooltip>
                                    <Tooltip title={experience.country}>
                                        <S.Info mobile={mobile}>{experience.country}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                {showRegion && (
                                    <S.InfoItem>
                                        <Tooltip title={t('region', { lng: language })}>
                                            <S.Label>{t('region', { lng: language })}</S.Label>
                                        </Tooltip>
                                        <Tooltip title={experience.region}>
                                            <S.Info mobile={mobile}>{experience.region}</S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                )}
                                <S.InfoItem>
                                    <Tooltip title={t('percentOfFulltimeWork', { lng: language })}>
                                        <S.Label mobile={mobile}>
                                            {t('percentOfFulltimeWork', { lng: language })}
                                        </S.Label>
                                    </Tooltip>
                                    <Tooltip title={experience.percent_time}>
                                        <S.Info mobile={mobile}>{experience.percent_time}</S.Info>
                                    </Tooltip>
                                </S.InfoItem>
                                <ProfileFields
                                    record={experience}
                                    keys={extraFieldKeys}
                                    mobile={mobile}
                                />
                            </div>
                        );
                    })}
                </>
            ) : !hasSpouseWorkExperienceData ? (
                <Result
                    icon={<WorkHistoryOutlined sx={{ fontSize: 32 }} />}
                    title={t('noInfo', { lng: language })}
                    description="No work experience has been added yet."
                />
            ) : null}
            {hasSpouseWorkExperienceData && (
                <>
                    {!spouseOnly && <S.Title>{t('partner', { lng: language })}</S.Title>}
                    {spouseWorkExperienceData?.map(
                        (experience: WorkExperienceData, index: number) => {
                            const showRegion = REGION_COUNTRIES.includes(
                                experience.country as string
                            );
                            const showWorkPermit = showRegion;

                            return (
                                <div key={index}>
                                    {spouseWorkExperienceData &&
                                        spouseWorkExperienceData.length > 1 && (
                                            <S.Title>{`${t('experience', { lng: language })} ${
                                                index + 1
                                            }`}</S.Title>
                                        )}
                                    <S.InfoItem>
                                        <Tooltip title={t('startDate', { lng: language })}>
                                            <S.Label>{t('startDate', { lng: language })}</S.Label>
                                        </Tooltip>
                                        <Tooltip title={formatDate(experience.start)}>
                                            <S.Info mobile={mobile}>
                                                {formatDate(experience.start)}
                                            </S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                    <S.InfoItem>
                                        <Tooltip title={t('currentEmployment', { lng: language })}>
                                            <S.Label>
                                                {t('currentEmployment', { lng: language })}
                                            </S.Label>
                                        </Tooltip>
                                        <Tooltip
                                            title={
                                                experience.current
                                                    ? t('yes', { lng: language })
                                                    : t('no', { lng: language })
                                            }
                                        >
                                            <S.Info mobile={mobile}>
                                                {experience.current
                                                    ? t('yes', { lng: language })
                                                    : t('no', { lng: language })}
                                            </S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                    <S.InfoItem>
                                        <Tooltip title={t('endDate', { lng: language })}>
                                            <S.Label>{t('endDate', { lng: language })}</S.Label>
                                        </Tooltip>
                                        <Tooltip title={formatDate(experience.end)}>
                                            <S.Info mobile={mobile}>
                                                {formatDate(experience.end)}
                                            </S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                    {showWorkPermit && (
                                        <S.InfoItem>
                                            <Tooltip title={t('employerPermit', { lng: language })}>
                                                <S.Label mobile={mobile}>
                                                    {t('employerPermit', { lng: language })}
                                                </S.Label>
                                            </Tooltip>
                                            <Tooltip title={experience.work_permit}>
                                                <S.Info mobile={mobile}>
                                                    {experience.work_permit}
                                                </S.Info>
                                            </Tooltip>
                                        </S.InfoItem>
                                    )}
                                    <S.InfoItem>
                                        <Tooltip title={t('country', { lng: language })}>
                                            <S.Label>{t('country', { lng: language })}</S.Label>
                                        </Tooltip>
                                        <Tooltip title={experience.country}>
                                            <S.Info mobile={mobile}>{experience.country}</S.Info>
                                        </Tooltip>
                                    </S.InfoItem>

                                    {showRegion && (
                                        <S.InfoItem>
                                            <Tooltip title={t('region', { lng: language })}>
                                                <S.Label>{t('region', { lng: language })}</S.Label>
                                            </Tooltip>
                                            <Tooltip title={experience.region}>
                                                <S.Info mobile={mobile}>{experience.region}</S.Info>
                                            </Tooltip>
                                        </S.InfoItem>
                                    )}
                                    <S.InfoItem>
                                        <Tooltip
                                            title={t('percentOfFulltimeWork', { lng: language })}
                                        >
                                            <S.Label mobile={mobile}>
                                                {t('percentOfFulltimeWork', { lng: language })}
                                            </S.Label>
                                        </Tooltip>
                                        <Tooltip title={experience.percent_time}>
                                            <S.Info mobile={mobile}>
                                                {experience.percent_time}
                                            </S.Info>
                                        </Tooltip>
                                    </S.InfoItem>
                                </div>
                            );
                        }
                    )}
                </>
            )}
        </>
    );
};

export default WorkExperience;
